import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccountWithProjectsByDateDTO } from 'entities/account';
import { findNewGrayedOut, getKeysFromAccounts, removeKeyFromAccounts } from './accountTreeUtils';

interface UseGrayedOutAccountsParams {
  accountsAndProjects: AccountWithProjectsByDateDTO[];
  checkedKeys: string[];
  isSearching: boolean;
}

interface UseGrayedOutAccountsResult {
  grayedOutAccounts: AccountWithProjectsByDateDTO[];
  fullAvailableKeys: Set<string>;
  handleRemoveGrayedOut: (key: string) => void;
}

/**
 * Hook to manage grayed out accounts logic.
 * Tracks accounts that were selected but are no longer in the API response.
 */
export function useGrayedOutAccounts({
  accountsAndProjects,
  checkedKeys,
  isSearching,
}: UseGrayedOutAccountsParams): UseGrayedOutAccountsResult {
  const [grayedOutAccounts, setGrayedOutAccounts] = useState<AccountWithProjectsByDateDTO[]>([]);

  // Track accounts seen during search session
  const searchSessionAccountsRef = useRef<Map<string, AccountWithProjectsByDateDTO>>(new Map());
  // Track last non-search accounts for comparison
  const lastNonSearchAccountsRef = useRef<AccountWithProjectsByDateDTO[]>([]);
  // Track if we were searching in the previous render
  const wasSearchingRef = useRef(isSearching);

  // Compute API keys
  const apiKeys = useMemo(() => getKeysFromAccounts(accountsAndProjects), [accountsAndProjects]);

  // Store fullAvailableKeys in a ref to persist during search
  const fullAvailableKeysRef = useRef<Set<string>>(new Set());

  const fullAvailableKeys = useMemo(() => {
    if (isSearching) {
      return fullAvailableKeysRef.current;
    }
    const combined = new Set<string>();
    apiKeys.forEach((k) => combined.add(k));
    getKeysFromAccounts(grayedOutAccounts).forEach((k) => combined.add(k));
    fullAvailableKeysRef.current = combined;
    return combined;
  }, [apiKeys, grayedOutAccounts, isSearching]);

  // Helper to check if grayed out accounts actually changed
  const hasGrayedOutChanged = useCallback(
    (newGrayedOut: AccountWithProjectsByDateDTO[]): boolean => {
      const currentIds = grayedOutAccounts
        .map((a) => a.id)
        .sort()
        .join(',');
      const newIds = newGrayedOut
        .map((a) => a.id)
        .sort()
        .join(',');
      return currentIds !== newIds;
    },
    [grayedOutAccounts],
  );

  // Main effect to track and update grayed out accounts
  useEffect(() => {
    if (isSearching) {
      // During search: accumulate all seen accounts in ref (no state update)
      accountsAndProjects.forEach((acc) => {
        searchSessionAccountsRef.current.set(acc.id, acc);
      });
      wasSearchingRef.current = true;
      return;
    }

    if (wasSearchingRef.current) {
      // Search just ended
      wasSearchingRef.current = false;

      const prevAccounts = lastNonSearchAccountsRef.current;
      const searchSessionAccounts = Array.from(searchSessionAccountsRef.current.values());

      // Combine previous non-search accounts with accounts seen during search
      const allPreviousAccounts = [...prevAccounts];
      searchSessionAccounts.forEach((acc) => {
        if (!allPreviousAccounts.some((a) => a.id === acc.id)) {
          allPreviousAccounts.push(acc);
        }
      });

      if (allPreviousAccounts.length > 0) {
        const newGrayedOut = findNewGrayedOut(allPreviousAccounts, accountsAndProjects, grayedOutAccounts, checkedKeys);
        if (hasGrayedOutChanged(newGrayedOut)) {
          setGrayedOutAccounts(newGrayedOut);
        }
      }

      lastNonSearchAccountsRef.current = accountsAndProjects;
      searchSessionAccountsRef.current.clear();
      return;
    }

    // Normal period/filter change (not searching, wasn't searching)
    const prevAccounts = lastNonSearchAccountsRef.current;

    if (prevAccounts.length > 0) {
      const newGrayedOut = findNewGrayedOut(prevAccounts, accountsAndProjects, grayedOutAccounts, checkedKeys);
      if (hasGrayedOutChanged(newGrayedOut)) {
        setGrayedOutAccounts(newGrayedOut);
      }
    }

    lastNonSearchAccountsRef.current = accountsAndProjects;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsAndProjects, isSearching]);

  // Handler to remove a key from grayed out accounts
  const handleRemoveGrayedOut = useCallback((key: string) => {
    setGrayedOutAccounts((prev) => removeKeyFromAccounts(prev, key));
  }, []);

  return {
    grayedOutAccounts,
    fullAvailableKeys,
    handleRemoveGrayedOut,
  };
}
